import { useEffect } from 'react';
import { subscribe } from 'lib/pusher';
import type {
  PreDrillWorkspace,
  PreDrillTargetZoneStatus,
  PreDrillGeometryStatus,
} from 'types/api-responses';

interface PreDrillNotificationHandlers {
  /** Success completion landed — re-fetch the thin show + sections. Receives the
   *  event name so the caller can clear that pending subscription. */
  onShowChanged: (eventName: string) => void;
  /** Failure completion landed — surface the error (no re-fetch). The event name
   *  lets the caller route the failure to the right section. */
  onFailure?: (
    error: { code?: string; message?: string } | null,
    eventName: string
  ) => void;
}

// Pusher payload (camelCase) shared by two polymorphic events, discriminated by
// `status`: `processed`→resources, `rejected`→reviewReason, `failed`→error.
interface PreDrillPushPayload {
  status?: 'processed' | 'rejected' | 'failed';
  workspaceId?: number;
  /** target_zone_generation `rejected` — e.g. below_confidence_threshold. */
  reviewReason?: string;
  resources?: {
    href?: string;
    /** target_zone_generation success/rejected. */
    suggestionId?: number | null;
    /** geometry_construction success — the built geometry row to chart. */
    preDrillWellSurveyGeometryId?: number;
    generatedAt?: string;
  };
  error?: { code?: string; message?: string } | null;
}

const TARGET_ZONE_GENERATION = 'target_zone_generation';
const GEOMETRY_CONSTRUCTION = 'geometry_construction';

// Which channel events can still fire given the current lifecycle status, per
// the FE subscription matrix (target-zone status + geometry status).
function eventsToBind(
  targetZoneStatus: PreDrillTargetZoneStatus,
  geometryStatus: PreDrillGeometryStatus
): string[] {
  const events = new Set<string>();
  if (targetZoneStatus !== 'confirmed') {
    events.add(TARGET_ZONE_GENERATION);
    if (targetZoneStatus === 'processed') events.add(GEOMETRY_CONSTRUCTION);
  } else {
    // Confirmed: geometry is building (or already built — Re-run/Refresh).
    events.add(GEOMETRY_CONSTRUCTION);
    if (geometryStatus === 'processed') events.add(TARGET_ZONE_GENERATION);
  }
  // A geometry build can be in flight while the agent status stays non-terminal
  // (e.g. manual confirm), so bind the completion event off the geometry status.
  if (geometryStatus === 'pending' || geometryStatus === 'processing') {
    events.add(GEOMETRY_CONSTRUCTION);
  }
  return [...events];
}

// Subscribes the workspace to its Pusher channel + binds status-appropriate
// completion events: success → re-fetch, failure → surface error.
export const usePreDrillNotifications = (
  workspace: PreDrillWorkspace,
  { onShowChanged, onFailure }: PreDrillNotificationHandlers,
  // Events awaited after enqueuing a Recalculate / Re-run — subscribe
  // immediately, since the BE flips status to processing only asynchronously.
  pendingEvents: string[] = []
): void => {
  // An untouched bore has no workspace yet, so no channel; nothing subscribes until it does.
  const { channel, shouldSubscribe } = workspace.resources.notifications ?? {};
  const { targetZoneStatus, geometryStatus } = workspace;
  // Subscribe while a background job can still push: either a status says
  // pending/processing, or we just enqueued one (pendingEvents).
  const hasInFlightWork =
    targetZoneStatus === 'pending' ||
    targetZoneStatus === 'processing' ||
    geometryStatus === 'pending' ||
    geometryStatus === 'processing';
  const wantsSubscription = hasInFlightWork || pendingEvents.length > 0;
  // Bind the status-appropriate events plus any enqueued (pending) ones.
  const boundEvents = Array.from(
    new Set([
      ...eventsToBind(targetZoneStatus, geometryStatus),
      ...pendingEvents,
    ])
  ).join(',');

  useEffect(() => {
    // `shouldSubscribe` is an optional server gate; when the BE omits it we
    // subscribe based on lifecycle. Only an explicit `false` opts out.
    if (shouldSubscribe === false || !channel || !wantsSubscription) {
      return undefined;
    }

    const subscription = subscribe(channel);
    const names = boundEvents ? boundEvents.split(',') : [];
    // One handler per event so the failure callback knows which event failed
    // (geometry vs. target-zone) and can route the banner accordingly.
    const handler = (eventName: string) => (data: PreDrillPushPayload) => {
      if (data?.status === 'failed') {
        onFailure?.(data.error ?? null, eventName);
        return;
      }
      // `processed` and `rejected` both settle the workspace — re-fetch the
      // thin show so the sections reflect the new state.
      onShowChanged(eventName);
    };
    const bound = names.map((name) => ({ name, fn: handler(name) }));

    bound.forEach(({ name, fn }) => subscription.bind(name, fn));

    return () => {
      bound.forEach(({ name, fn }) => subscription.unbind(name, fn));
      subscription.unsubscribe();
    };
  }, [
    channel,
    shouldSubscribe,
    wantsSubscription,
    boundEvents,
    onShowChanged,
    onFailure,
  ]);
};
